import { Box, Stack } from '@mui/material';
import { useMemo } from 'react';

import { File05Icon } from '@/components/icons/File05Icon';
import { FileSearch02DuotoneIcon } from '@/components/icons/FileSearch02DuotoneIcon';
import { FileSearch02Icon } from '@/components/icons/FileSearch02Icon';
import { FileShield03DuotoneIcon } from '@/components/icons/FileShield03DuotoneIcon';
import { Globe03DuotoneIcon } from '@/components/icons/Globe03DuotoneIcon';
import { Home03DuotoneIcon } from '@/components/icons/Home03DuotoneIcon';
import { PieChart03DuotoneIcon } from '@/components/icons/PieChart03DuotoneIcon';
import { PieChart03Icon } from '@/components/icons/PieChart03Icon';
import { PresentationChart01Icon } from '@/components/icons/PresentationChart01Icon';
import { TableDuotoneIcon } from '@/components/icons/TableDuotoneIcon';
import {
  DeliverableCard,
  DeliverableCardProps,
} from '@/components/layout/DeliverableCard/DeliverableCard';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { CLIENT_ONBOARDING_DESCRIPTIONS } from '@/modules/clientOnboarding/constants';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

import { getIntegrationProviderDisplayName } from '../assetProviderIntegrations/shared/constants';
import Estate360SVG from './components/Estate360.svg?react';

export interface ClientOnboardingDeliverableCardProps
  extends Partial<DeliverableCardProps> {
  sizeVariant: 'large' | 'small';
  onClick: () => void;
}

type LargeDeliverableCardProps = Omit<
  ClientOnboardingDeliverableCardProps,
  'sizeVariant'
>;

export function ClientOnboardingPresentationCard({
  sizeVariant,
  onClick,
  ...props
}: ClientOnboardingDeliverableCardProps) {
  return (
    <DeliverableCard
      title="Presentation"
      description="Assemble firm-branded comprehensive presentations"
      icon={PresentationChart01Icon}
      sizeVariant={sizeVariant}
      onClick={onClick}
      {...props}
    />
  );
}

export function ClientOnboardingMemoCard({
  sizeVariant,
  onClick,
  ...props
}: ClientOnboardingDeliverableCardProps) {
  return (
    <DeliverableCard
      title="Memo"
      description="Draft client-ready cross-plan memos from household data"
      icon={File05Icon}
      sizeVariant={sizeVariant}
      onClick={onClick}
      {...props}
    />
  );
}

export function ClientOnboardingDocumentSummaryCard({
  sizeVariant,
  onClick,
  ...props
}: ClientOnboardingDeliverableCardProps) {
  return (
    <DeliverableCard
      title="Document summary"
      description={CLIENT_ONBOARDING_DESCRIPTIONS.SUMMARIZE_DOCUMENTS}
      timeEstimateLabel="5 min"
      icon={
        sizeVariant === 'large' ? FileSearch02DuotoneIcon : FileSearch02Icon
      }
      sizeVariant={sizeVariant}
      onClick={onClick}
      {...props}
    />
  );
}

export function ClientOnboardingBalanceSheetCard({
  onClick,
  ...props
}: LargeDeliverableCardProps) {
  const { linkedHouseholdAssetProvider } = useHouseholdDetailsContext();
  const { householdId } = useHouseholdDetailsContext();

  const customBadge = useMemo(() => {
    if (!linkedHouseholdAssetProvider) {
      // No custom badge; will use the default "AI Assist" badge
      return undefined;
    }
    return (
      <Badge
        variant={BadgeVariants.SecondaryFilled}
        display={getIntegrationProviderDisplayName(
          linkedHouseholdAssetProvider
        )}
      />
    );
  }, [linkedHouseholdAssetProvider]);

  if (!householdId) {
    return null;
  }

  return (
    <DeliverableCard
      title="Balance sheet"
      description={CLIENT_ONBOARDING_DESCRIPTIONS.CREATE_BALANCE_SHEET}
      timeEstimateLabel="5 min"
      icon={TableDuotoneIcon}
      sizeVariant="large"
      badgeComponent={customBadge}
      onClick={onClick}
      {...props}
    />
  );
}

export function ClientOnboardingEntityCard({
  onClick,
  ...props
}: LargeDeliverableCardProps) {
  return (
    <DeliverableCard
      title="Entity overviews"
      description={CLIENT_ONBOARDING_DESCRIPTIONS.CREATE_ENTITY_OVERVIEW}
      timeEstimateLabel="10 min"
      icon={FileShield03DuotoneIcon}
      sizeVariant="large"
      onClick={onClick}
      {...props}
    />
  );
}

export function ClientOnboardingHouseholdCard({
  onClick,
  ...props
}: LargeDeliverableCardProps) {
  return (
    <DeliverableCard
      title="Full client profile"
      description={CLIENT_ONBOARDING_DESCRIPTIONS.BUILD_OUT_HOUSEHOLD}
      timeEstimateLabel="15 min"
      icon={Home03DuotoneIcon}
      sizeVariant="large"
      onClick={onClick}
      {...props}
    />
  );
}

export function ClientOnboardingAnalysisCard({
  sizeVariant,
  onClick,
  ...props
}: ClientOnboardingDeliverableCardProps) {
  return (
    <DeliverableCard
      title="Strategy analysis"
      description={CLIENT_ONBOARDING_DESCRIPTIONS.CREATE_ANALYSIS}
      timeEstimateLabel="5 min"
      icon={sizeVariant === 'large' ? PieChart03DuotoneIcon : PieChart03Icon}
      sizeVariant={sizeVariant}
      badgeComponent={<Box />}
      onClick={onClick}
      {...props}
    />
  );
}

export function ClientOnboardingEstateSnapshotCard({
  onClick,
  ...props
}: LargeDeliverableCardProps) {
  return (
    <DeliverableCard
      title={
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Estate360SVG height={20} width={20} />
          <Box>Estate360</Box>
        </Stack>
      }
      description={CLIENT_ONBOARDING_DESCRIPTIONS.GENERATE_ESTATE_SNAPSHOT}
      timeEstimateLabel="2 min"
      icon={Globe03DuotoneIcon}
      sizeVariant="large"
      onClick={onClick}
      {...props}
    />
  );
}
